'use client'

import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'

import type { MenuItem, MenuSeparator, NavGroup } from '@graftcode/design-system'
import { Icon, IconComponent, SearchBar, Tree, TreeItem } from '@graftcode/design-system'

import { AppVariant } from '../../../../enums'
import { BaseMobileMenu } from '../../BaseMobileMenu'

import styles from '../../MobileMenu.module.scss'

// Get all item IDs recursively for expanding entire tree
const getAllItemIds = (items: TreeItem[]): string[] =>
  items.flatMap((item) => [item.id, ...(item.children ? getAllItemIds(item.children) : [])])

interface VisionMobileMenuProps {
  /** Whether the menu is open */
  isOpen: boolean
  /** Callback to close the menu */
  onClose: () => void
  /** Navigation groups */
  menuGroups: NavGroup[]
  /** Items for the Help dropdown / drawer */
  helpItems?: (MenuItem | MenuSeparator)[]
  /** Handler for profile click */
  onProfileClick?: (itemId: string) => void
  /** Handler for help item click */
  onHelpClick?: (itemId: string) => void
  /** Handler for navigation click */
  onNavigationClick?: (url: string) => void
  /** Handler for project selection */
  onProjectClick?: (projectId: string) => void
  /** Handler for workspace selection */
  onWorkspaceClick?: (workspaceId: string) => void
  /** Tree items for Vision navigation */
  treeItems?: TreeItem[]
  /** Active item ID in tree */
  activeItemId?: string
  /** Handler for tree item click */
  onTreeItemClick?: (item: TreeItem) => void
  /** Handler for tree item toggle */
  onTreeItemToggle?: (item: TreeItem, expanded: boolean) => void
  /** Whether data is loading */
  isLoading?: boolean
  /** Whether there's an error */
  isError?: boolean
  /** Handler to open workspace settings */
  onOpenWorkspaceSettings?: () => void
  /** Handler to open project settings */
  onOpenProjectSettings?: () => void
  /** Handler to open project members */
  onOpenProjectMembers?: () => void
  /** Handler to trigger project invite flow */
  onInviteProjectClick?: () => void
  /** Handler to trigger workspace invite flow */
  onInviteWorkspaceClick?: () => void
  /** Handler to open create project flow */
  onCreateProject?: () => void
  notificationBadge?: number
  onNotificationClick?: () => void
}

export function VisionMobileMenu({
  isOpen,
  onClose,
  menuGroups,
  helpItems,
  onProfileClick,
  onHelpClick,
  onNavigationClick,
  onProjectClick,
  onWorkspaceClick,
  treeItems = [],
  activeItemId,
  onTreeItemClick,
  onTreeItemToggle,
  isLoading = false,
  isError = false,
  onOpenWorkspaceSettings,
  onOpenProjectSettings,
  onOpenProjectMembers,
  onInviteProjectClick,
  onInviteWorkspaceClick,
  onCreateProject,
  notificationBadge,
  onNotificationClick,
}: VisionMobileMenuProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [localExpandedIds, setLocalExpandedIds] = useState<string[]>(['root'])

  // Get parent IDs from an item path (e.g., "MyLib/MyClass/method" -> ["root", "MyLib", "MyLib/MyClass"])
  const getParentIds = useCallback((itemId: string | undefined): string[] => {
    if (!itemId) return ['root']
    const parts = itemId.split('/')
    const parentIds: string[] = ['root']
    // Build parent paths: "MyLib", "MyLib/MyClass", etc.
    for (let i = 0; i < parts.length; i++) {
      parentIds.push(parts.slice(0, i + 1).join('/'))
    }
    return parentIds
  }, [])

  // When menu opens, expand to show the currently active item
  useEffect(() => {
    if (isOpen && activeItemId) {
      const parentIds = getParentIds(activeItemId)
      setLocalExpandedIds(parentIds)
    }
  }, [isOpen, activeItemId, getParentIds])

  // Reset search when menu closes
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setSearchQuery('')
      }, 300)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [isOpen])

  // Filter tree items based on search
  const filteredTreeItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return treeItems
    }

    const filterItems = (items: TreeItem[]): TreeItem[] =>
      items.reduce<TreeItem[]>((acc, item) => {
        const matchesSearch = item.label.toLowerCase().includes(searchQuery.toLowerCase())
        const filteredChildren = item.children ? filterItems(item.children) : []

        if (matchesSearch || filteredChildren.length > 0) {
          acc.push({
            ...item,
            children: filteredChildren.length > 0 ? filteredChildren : item.children,
          })
        }

        return acc
      }, [])

    return filterItems(treeItems)
  }, [treeItems, searchQuery])

  // When searching, expand all filtered items; otherwise use local expanded state
  const effectiveExpandedIds = useMemo(() => {
    if (searchQuery.trim()) {
      // When searching, expand all to show results
      return getAllItemIds(filteredTreeItems)
    }
    // When not searching, use locally tracked expanded items
    return localExpandedIds
  }, [searchQuery, filteredTreeItems, localExpandedIds])

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => setSearchQuery(event.target.value)

  const handleSearchClear = () => setSearchQuery('')

  const handleItemClick = useCallback(
    (item: TreeItem) => {
      // If item has children, expand it when clicked
      const hasChildren = item.children && item.children.length > 0
      if (hasChildren) {
        setLocalExpandedIds((prev) => {
          if (!prev.includes(item.id)) {
            return [...prev, item.id]
          }
          return prev
        })
      }
      onTreeItemClick?.(item)
    },
    [onTreeItemClick]
  )

  const handleItemToggle = useCallback(
    (item: TreeItem, expanded: boolean) => {
      // Update local expanded state
      setLocalExpandedIds((prev) => {
        if (expanded) {
          return [...prev, item.id]
        } else {
          return prev.filter((id) => id !== item.id)
        }
      })
      // Also call parent handler
      onTreeItemToggle?.(item, expanded)
    },
    [onTreeItemToggle]
  )

  // Create unique tree key for re-renders
  const treeKey = `${activeItemId}::${effectiveExpandedIds.join(',')}::${searchQuery}`

  // Render tree navigation slot
  const renderTreeNavigation = () => {
    // Show error state when data fetch failed
    if (isError) {
      return (
        <div className={styles.visionTreeNoResults}>
          <IconComponent icon={Icon.ErrorCircleRegular} className={styles.visionTreeNoResultsIcon} />
          <span className={styles.visionTreeNoResultsTitle}>Failed to load libraries</span>
          <span className={styles.visionTreeNoResultsMessage}>Please try again later</span>
        </div>
      )
    }

    // Don't render while loading or when no data
    if (isLoading || treeItems.length === 0) {
      return null
    }

    return (
      <div className={styles.visionTreeContainer}>
        <SearchBar
          className={styles.visionTreeSearch}
          value={searchQuery}
          onChange={handleSearchChange}
          onClear={handleSearchClear}
          size="md"
          version="gray"
          placeholder="Search libraries..."
        />
        {filteredTreeItems.length > 0 ? (
          <Tree
            key={treeKey}
            className={styles.visionTree}
            items={filteredTreeItems}
            expandedItemIds={effectiveExpandedIds}
            activeItemId={activeItemId}
            showBadges
            onItemClick={handleItemClick}
            onItemToggle={handleItemToggle}
          />
        ) : searchQuery.trim() ? (
          <div className={styles.visionTreeNoResults}>
            <IconComponent icon={Icon.SearchRegular} className={styles.visionTreeNoResultsIcon} />
            <span className={styles.visionTreeNoResultsTitle}>No results found</span>
            <span className={styles.visionTreeNoResultsMessage}>Try a different search term</span>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <BaseMobileMenu
      isOpen={isOpen}
      onClose={onClose}
      appVariant={AppVariant.VISION}
      menuGroups={menuGroups}
      helpItems={helpItems}
      onProfileClick={onProfileClick}
      onHelpClick={onHelpClick}
      onNavigationClick={onNavigationClick}
      onProjectClick={onProjectClick}
      onWorkspaceClick={onWorkspaceClick}
      onOpenWorkspaceSettings={onOpenWorkspaceSettings}
      onOpenProjectSettings={onOpenProjectSettings}
      onOpenProjectMembers={onOpenProjectMembers}
      onInviteProjectClick={onInviteProjectClick}
      onInviteWorkspaceClick={onInviteWorkspaceClick}
      onCreateProject={onCreateProject}
      notificationBadge={notificationBadge}
      onNotificationClick={onNotificationClick}
      navigationTopSlot={renderTreeNavigation()}
    />
  )
}
